import React from 'react';
import { Upload, X, FileText, Image } from 'lucide-react';

/**
 * Componente reutilizable para subida de archivos
 * Soporta drag & drop, preview de imágenes, validación de tipo y tamaño
 */
const FileUpload = ({
    label,
    name,
    file,
    onChange,
    accept = "image/*,.pdf",
    required = false,
    error = ""
}) => {
    const inputRef = React.useRef(null);
    const [dragActive, setDragActive] = React.useState(false);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFileChange(e.target.files[0]);
        }
    };

    const handleFileChange = (selectedFile) => {
        // Validar tamaño (5MB máximo)
        if (selectedFile.size > 5 * 1024 * 1024) {
            alert('El archivo es demasiado grande. Tamaño máximo: 5MB');
            return;
        }

        onChange(name, selectedFile);
    };

    const handleRemove = () => {
        onChange(name, null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const isImage = file && file.type && file.type.startsWith('image/');
    const isPDF = file && file.type === 'application/pdf';

    return (
        <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {!file ? (
                <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            ${error ? 'border-red-500' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => inputRef.current?.click()}
                >
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                        Arrastra un archivo aquí o haz clic para seleccionar
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        Máximo 5MB
                    </p>
                    <input
                        ref={inputRef}
                        type="file"
                        name={name}
                        accept={accept}
                        onChange={handleChange}
                        className="hidden"
                    />
                </div>
            ) : (
                <div className="border-2 border-gray-300 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {isImage && (
                                <img
                                    src={URL.createObjectURL(file)}
                                    alt="Preview"
                                    className="w-16 h-16 object-cover rounded"
                                />
                            )}
                            {isPDF && (
                                <div className="w-16 h-16 bg-red-100 rounded flex items-center justify-center">
                                    <FileText className="w-8 h-8 text-red-600" />
                                </div>
                            )}
                            {!isImage && !isPDF && (
                                <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                                    <Image className="w-8 h-8 text-gray-600" />
                                </div>
                            )}
                            <div>
                                <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                                    {file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {(file.size / 1024).toFixed(2)} KB
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="text-red-500 hover:text-red-700 p-1"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>
            )}

            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
};

export default FileUpload;
